import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Form,
  FormGroup,
  FormFeedback,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { APIimportTerms } from '../../../api/Metadata';

import {
  formatBytes,
  getFileNameFromPath,
} from '../../../helpers/Files';

import ProcessingSpinner from '../../ProcessingSpinner';

import ErrorBoundary from '../../ErrorBoundary';

class TermsImportModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      file: null,
      fileSize: null,
      replace: false,
      importedCount: 0,
      success: false,
      error: false,
      saving: false,
    };

    this.maxFileSize = 52428800;
    this.fileInputRef = React.createRef();

    this.handleChange = this.handleChange.bind(this);
    this.handleImport = this.handleImport.bind(this);
  }

  handleImport() {
    const {
      intl,
      taxonomy,
      afterImportTerms,
    } = this.props;
    const {
      file,
      replace,
    } = this.state;

    if (!file) {
      this.setState({ error: intl.formatMessage({ defaultMessage: 'You need to select a file to import' }) });
      return;
    }

    this.setState({
      error: false,
      saving: true,
    });

    const fileReader = new FileReader();
    fileReader.readAsText(this.fileInputRef.files[0]);
    fileReader.onloadend = (e) => {
      const content = btoa(unescape(encodeURIComponent(e.target.result)));
      APIimportTerms(taxonomy.id, content, {
        replace,
        file_name: getFileNameFromPath(file),
      }).then((response) => {
        afterImportTerms();
        this.setState({
          success: true,
          importedCount: response.data.imported,
        });
      });
    };
  }

  handleChange(e) {
    const { intl } = this.props;
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    if (type === 'checkbox') {
      this.setState({ [name]: checked });
    } else {
      this.setState({ [name]: value });
      if (name === 'file') {
        this.setState({
          error: false,
          fileSize: this.fileInputRef.files[0].size,
        }, () => {
          const { fileSize } = this.state;
          if (fileSize > this.maxFileSize) {
            this.setState({
              error: intl.formatMessage(
                { defaultMessage: 'Your file is too large ({fileSize}) - the maximum size is {maxFileSize}' },
                { fileSize: formatBytes(fileSize), maxFileSize: formatBytes(this.maxFileSize) },
              ),
            });
          }
        });
      }
    }
  }

  render() {
    const { intl, handleClose } = this.props;
    const {
      file,
      replace,
      importedCount,
      error,
      success,
      saving,
    } = this.state;

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {intl.formatMessage({ defaultMessage: 'Import file tags' })}
        </ModalHeader>
        <ModalBody>
          {success ? (
            <div className="center">
              <p>
                {intl.formatMessage(
                  { defaultMessage: 'You have imported {importedCount} {importedCount, plural, one {tag} other {tags}}' },
                  { importedCount },
                )}
              </p>
              <Button
                color="secondary"
                onClick={handleClose}
              >
                {intl.formatMessage({ defaultMessage: 'Close' })}
              </Button>
            </div>
          ) : (
            <Form className="import-terms-form" onSubmit={(e) => { e.preventDefault(); this.handleImport(); }}>
              <p>
                {intl.formatMessage({
                  defaultMessage: `Import predefined tags for use in your Tag Categories.{br}
                    This import tool accepts CSV (comma-separated) or
                    TXT files only with one tag per line/row.`,
                }, { br: <br /> })}
              </p>
              <FormGroup>
                <Label for="importFile">
                  {intl.formatMessage({ defaultMessage: 'File to import' })}
                </Label>
                <Input
                  type="file"
                  id="importFile"
                  name="file"
                  accept=".csv,.txt"
                  innerRef={(ref) => { this.fileInputRef = ref; }}
                  disabled={saving}
                  invalid={!!error}
                  onChange={this.handleChange}
                />
                <FormFeedback>{error}</FormFeedback>
              </FormGroup>
              <FormGroup check>
                <Label for="importReplace" check>
                  <Input
                    type="checkbox"
                    id="importReplace"
                    name="replace"
                    disabled={saving}
                    onChange={this.handleChange}
                  />
                  {intl.formatMessage({ defaultMessage: 'Delete existing tags' })}
                </Label>
              </FormGroup>
              {replace && (
                <p className="error">
                  <strong>
                    {intl.formatMessage({
                      defaultMessage: `This will delete all existing tags in this category,
                        throughout all files in your workspace.
                        This can not be undone!`,
                    })}
                  </strong>
                </p>
              )}
            </Form>
          )}
        </ModalBody>
        {!success && (
          <ModalFooter>
            <div className="buttons-group">
              <Button
                color="secondary"
                disabled={saving}
                onClick={handleClose}
              >
                {intl.formatMessage({ defaultMessage: 'Cancel' })}
              </Button>
              <Button
                color="primary"
                disabled={saving || !file || !!error}
                onClick={this.handleImport}
              >
                <ProcessingSpinner show={saving} />
                {intl.formatMessage({ defaultMessage: 'Import' })}
              </Button>
            </div>
          </ModalFooter>
        )}
      </ErrorBoundary>
    );
  }
}

TermsImportModal.propTypes = {
  intl: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  taxonomy: PropTypes.object.isRequired,
  afterImportTerms: PropTypes.func,
};

TermsImportModal.defaultProps = {
  afterImportTerms: () => {},
};

export default injectIntl(TermsImportModal);
